import React, { useState, useEffect, useRef, useCallback, Suspense } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import axios from "axios";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { useDropzone } from 'react-dropzone';

const BarcodeScanner = React.lazy(() => import("./BarcodeScanner"));

const ProductModalContent = React.forwardRef(({ shopId, productId, onSaveSuccess }, ref) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const textFieldRef = useRef(null);

  const [formData, setFormData] = useState({
    shopId: shopId,
    name: "",
    description: "",
    price: "",
    url: "",
    barCode: "",
    sku: "",
    categoryId: "",
  });
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  const [cameraDeviceId, setCameraDeviceId] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    setImageFiles(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxFiles: 5,
  });

  React.useImperativeHandle(ref, () => ({
    handleClickOpen,
    handleClose,
  }));

  useEffect(() => {
    if (!open) return;
    axios.get("/api/categories?pageSize=200&sortField=name&sortOrder=asc")
      .then(res => setCategories(res.data.categories || []))
      .catch(err => console.error("Error loading categories:", err));

    axios.get("/api/tags?pageSize=200&sortField=name&sortOrder=asc")
      .then(res => setTags(res.data.tags || []))
      .catch(err => console.error("Error loading tags:", err));
  }, [open]);

  useEffect(() => {
    if (productId) {
      axios.get(process.env.REACT_APP_API_URL + `/api/products/${productId}`)
        .then(response => {
          const productData = response.data;
          setFormData({
            name: productData.name || "",
            description: productData.description || "",
            price: productData.price || "",
            url: productData.url || "",
            barCode: productData.barCode || "",
            sku: productData.sku || "",
            categoryId: productData.categoryId || "",
          });
          setExistingImages(productData.images || []);
          setSelectedTags(productData.tags || []);
          setImageFiles([]);
        })
        .catch(error => {
          console.error(`Error fetching product data for ID ${productId}:`, error);
          resetForm();
        });
    } else {
      resetForm();
    }
  }, [productId]);

  const resetForm = () => {
    setFormData({
      shopId: shopId,
      name: "",
      description: "",
      price: "",
      url: "",
      barCode: "",
      sku: "",
      categoryId: "",
    });
    setImageFiles([]);
    setExistingImages([]);
    setSelectedTags([]);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setScannerOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleOpenScanner = async (target) => {
    setScanTarget(target);
    setScannerOpen(true);
    setCameraDeviceId(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const track = stream.getVideoTracks()[0];
      const deviceId = track.getSettings().deviceId;
      stream.getTracks().forEach((t) => t.stop());
      setCameraDeviceId(deviceId);
    } catch (err) {
      console.error("Camera pre-permission error:", err);
    }
  };

  const handleScan = (decodedText) => {
    if (scanTarget === "barCode") {
      setFormData(prev => ({ ...prev, barCode: decodedText }));
    } else if (scanTarget === "sku") {
      setFormData(prev => ({ ...prev, sku: decodedText }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const apiEndpoint = productId
      ? process.env.REACT_APP_API_URL + `/api/products/${productId}`
      : process.env.REACT_APP_API_URL + "/api/products";

    const tagIds = selectedTags.map(t => t.id);

    let payload;
    let headers = {};
    if (imageFiles && imageFiles.length > 0) {
      payload = new FormData();
      payload.append('name', formData.name);
      payload.append('description', formData.description);
      payload.append('price', formData.price);
      payload.append('url', formData.url);
      payload.append('barCode', formData.barCode);
      payload.append('sku', formData.sku);
      payload.append('categoryId', formData.categoryId);
      payload.append('tagIds', JSON.stringify(tagIds));
      imageFiles.forEach((f) => payload.append('images', f));
      headers['Content-Type'] = 'multipart/form-data';
    } else {
      payload = { ...formData, tagIds };
    }

    const httpMethod = productId ? axios.put : axios.post;

    httpMethod(apiEndpoint, payload, { headers })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          handleClose();
          if (onSaveSuccess) {
            onSaveSuccess();
          }
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      TransitionProps={{
        onEntered: () => {
          if (textFieldRef.current) {
            textFieldRef.current.focus();
          }
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          <FormattedMessage id={productId ? "app.products.edit.title" : "app.products.create.title"} />
        </span>
        <Box>
          <Tooltip title="Close">
            <IconButton aria-label="Close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <form style={{ marginTop: "10px" }} onSubmit={handleSubmit}>
          <Grid container direction={"column"} spacing={2} className={"settingsContainer"}>
            <Grid item>
              <TextField
                name="name"
                label="Titulo"
                fullWidth
                value={formData.name}
                onChange={handleChange}
                inputRef={textFieldRef}
              />
            </Grid>
            <Grid item>
              <TextField
                name="description"
                label="Descripcion"
                multiline
                rows={4}
                fullWidth
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
            <Grid item>
              <TextField
                name="price"
                label="Price"
                fullWidth
                value={formData.price}
                onChange={handleChange}
                type="number"
                inputProps={{ step: "0.01" }}
              />
            </Grid>
            <Grid item>
              <TextField
                name="url"
                label="Link"
                fullWidth
                value={formData.url}
                onChange={handleChange}
                type="url"
              />
            </Grid>
            <Grid item>
              <TextField
                name="barCode"
                label="Código de barras"
                fullWidth
                value={formData.barCode}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleOpenScanner("barCode")}
                        edge="end"
                        size="small"
                      >
                        <QrCodeScannerIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              <TextField
                name="sku"
                label="SKU"
                fullWidth
                value={formData.sku}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleOpenScanner("sku")}
                        edge="end"
                        size="small"
                      >
                        <QrCodeScannerIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item>
              <FormControl fullWidth>
                <InputLabel id="category-label">Categoría</InputLabel>
                <Select
                  labelId="category-label"
                  name="categoryId"
                  value={formData.categoryId}
                  label="Categoría"
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>Sin categoría</em>
                  </MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Autocomplete
                multiple
                options={tags}
                value={selectedTags}
                onChange={(event, newValue) => setSelectedTags(newValue)}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Tags" placeholder="Seleccionar tags" />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} key={option.id} />
                  ))
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item>
              <Box
                {...getRootProps()}
                sx={{ border: '1px dashed #ccc', p: 2, textAlign: 'center', cursor: 'pointer' }}
              >
                <input {...getInputProps()} />
                <Typography variant="body2">Arrastra imágenes aquí o haz click para seleccionar (hasta 5)</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {existingImages.map((url, idx) => (
                  <Box key={`existing-${idx}`} sx={{ width: 80, height: 80, overflow: 'hidden', border: '1px solid #eee' }}>
                    <img src={process.env.REACT_APP_API_URL + url} alt={`img-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
                {imageFiles.map((file, idx) => (
                  <Box key={`file-${idx}`} sx={{ width: 80, height: 80, overflow: 'hidden', border: '1px solid #eee' }}>
                    <img src={URL.createObjectURL(file)} alt={`file-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} autoFocus>Save</Button>
      </DialogActions>

      <Suspense fallback={null}>
        <BarcodeScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onScan={handleScan}
          deviceId={cameraDeviceId}
        />
      </Suspense>
    </Dialog>
  );
});

export default ProductModalContent;
